import { GetPlayersByPlayerExternalIdGamesDataResponse } from "@aura-frontend/omnibus-az-client";

import { LaunchDataset } from "@/assets/LaunchVisualization";

/**
 * Default grouping criteria for launch chart data when not specified.
 * Groups data by pitch type by default.
 */
export const DEFAULT_GROUP_BY = ["pitch_type", "is_out", "is_hit"];

/**
 * Subset of ChartsData type containing only the fields relevant for launch charts.
 * Properly types the launch data arrays to avoid casting and improve type safety.
 */
type ChartDataset = {
  pitch_type?: string | null;
  play_result?: string | null;
  points_launch?: Array<Array<number | string>> | null;
};

export const PitchTypeColors: Record<string, string> = {
  Fastball: "#000",
  Offspeed: "#00FF00",
  Curveball: "#0000FF",
  Slider: "#FF0000",
  Cutter: "#FFA500",
  Other: "#808080",
};

export const PlayResultColors: Record<string, string> = {
  Double: "#9B59B6",
  Error: "#FFFFFF",
  Sacrifice: "#FFFFFF",
  HomeRun: "#E91E63",
  Out: "#9E9E9E",
  Single: "#FF9800",
  Triple: "#FFEB3B",
};

/**
 * Transforms raw chart data from the API into the format expected by LaunchChartCard.
 *
 * This function filters out datasets without launch points and transforms the remaining
 * data into a standardized format with angle/speed coordinates and appropriate colors.
 *
 * @param data - Raw chart datasets from the API
 * @param groupBy - Array of grouping criteria (e.g., ["pitch_type"] or ["play_result"])
 * @returns Array of LaunchDataset objects ready for visualization
 *
 * @remarks
 * The transformation process includes:
 * - Filtering datasets to only include those with launch point data
 * - Converting launch point arrays ([angle, speed]) into structured objects
 * - Applying appropriate colors based on grouping (pitch type or play result)
 * - Handling null/undefined values with fallback to "Other" category
 *
 * @example
 * ```typescript
 * const datasets = transformLaunchData(rawData, ["pitch_type"]);
 * // Returns: [
 * //   { name: "Fastball", color: "#000", points: [{angle: 20, speed: 95}, ...] },
 * //   { name: "Slider", color: "#FF0000", points: [{angle: 15, speed: 88}, ...] }
 * // ]
 * ```
 */
export function transformLaunchData(
  data: GetPlayersByPlayerExternalIdGamesDataResponse | undefined,
  groupBy?: string[],
): LaunchDataset[] {
  if (!data || !Array.isArray(data)) return [];

  // Check if we're grouping by play_result or pitch_type
  const isGroupedByPlayResult = groupBy?.includes("play_result");

  return data
    .filter((dataset) => {
      // Only include datasets with launch points
      const launchData = (dataset as ChartDataset).points_launch;
      return launchData && Array.isArray(launchData) && launchData.length > 0;
    })
    .map((rawDataset) => {
      const dataset = rawDataset as ChartDataset;
      let points: { angle: number; speed: number }[] = [];

      const launchData = dataset.points_launch;
      if (launchData && Array.isArray(launchData)) {
        points = launchData.map((coords) => ({
          angle: Number(coords[0]),
          speed: Number(coords[1]),
        }));
      }

      const outsOrHits =
        "is_out" in dataset && dataset.is_out ? "Outs" : "Hits";
      const icon =
        "is_out" in dataset && dataset.is_out ? "triangle" : "circle";

      if (isGroupedByPlayResult) {
        const playResult = dataset.play_result || "Other";
        return {
          name: `${outsOrHits} vs ${playResult}`,
          color: PlayResultColors[playResult] || "#808080",
          points,
          icon,
        };
      } else {
        const pitchType = dataset.pitch_type || "Other";
        return {
          name: `${outsOrHits} vs ${pitchType}`,
          color: PitchTypeColors[pitchType] || "#808080",
          points,
          icon,
        };
      }
    });
}
